class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    // Sliders can nest - product cards render their own slider-component for the
    // swatch strip - so only claim parts that aren't owned by a nested slider
    const owned = (el) => el.closest("slider-component, slideshow-component") === this;

    this.slider = this.classList.contains("slideshow")
      ? this
      : Array.from(this.querySelectorAll('[id^="Slider-"]')).find(owned);

    this.sliderItems = Array.from(this.querySelectorAll('[id^="Slide-"]')).filter(owned);
    this.enableSliderLooping = false;
    this.currentPageElement = Array.from(this.querySelectorAll(".slider-counter--current")).find(owned);
    this.pageTotalElement = Array.from(this.querySelectorAll(".slider-counter--total")).find(owned);
    this.prevButton = Array.from(this.querySelectorAll('button[name="previous"]')).find(owned);
    this.nextButton = Array.from(this.querySelectorAll('button[name="next"]')).find(owned);
    this.enableDrag = this.dataset.draggable === "true";

    if (!this.slider) return;

    // Cache some expensive calculations
    this.sliderRect = this.slider.getBoundingClientRect();
    this.sliderFirstItem = this.sliderItems[0];
    this.sliderSecondItem = this.sliderItems[1];

    this.initPages();
    // Debounce the resize observer to prevent frequent calls to initPages
    const resizeObserver = new ResizeObserver(this.debounce(() => this.initPages(), 200));
    resizeObserver.observe(this.slider);

    // Add event listeners only once
    this.slider.addEventListener("scroll", this.update.bind(this), { passive: true });
    this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton?.addEventListener("click", this.onButtonClick.bind(this));
    this.addTouchedSlideState();

    if (this.enableDrag) {
      this.addClickAndDrag();
    }
  }

  // Debounce function to limit the frequency of a function being called
  debounce(func, delay) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  initPages() {
    // Cache DOM values and calculations for performance
    this.sliderItemsToShow = this.sliderItems.filter((element) => element.clientWidth > 0);

    if (this.sliderItemsToShow.length < 2) {
      // stops the buttons from flashing on next hover
      this.prevButton?.setAttribute('disabled', 'disabled');
      this.nextButton?.setAttribute('disabled', 'disabled');
      this.classList.remove('slider--has-overflow');
      return;
    }

    // Cache the offsetLeft calculations
    const firstItemOffsetLeft = this.sliderItemsToShow[0].offsetLeft;
    const secondItemOffsetLeft = this.sliderItemsToShow[1].offsetLeft;

    this.sliderItemOffset = secondItemOffsetLeft - firstItemOffsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - firstItemOffsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

    // Call the update method to refresh the state of the buttons and pages
    this.update();
  }

  update() {
    if (!this.slider) return;
    if (this.sliderItemsToShow.length === 0) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    // Trigger the slideChanged event only if the page has actually changed
    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    // Disable/enable prev/next buttons based on the current page
    if (!this.enableSliderLooping) {
      const isAtStart = this.slider.scrollLeft === 0;
      const isAtEnd = this.slider.scrollLeft + this.slider.clientWidth >= this.slider.scrollWidth;
      const hideButtons = isAtStart && isAtEnd;

      if (isAtStart && isAtEnd) {
        this.classList.remove('slider--has-overflow');
      } else {
        this.classList.add('slider--has-overflow');
      }

      if (this.isSlideVisible(this.sliderItemsToShow[0])) {
        this.prevButton?.setAttribute("disabled", "disabled");
      } else {
        this.prevButton?.removeAttribute("disabled");
      }
      if (this.prevButton) this.prevButton.style.display = hideButtons ? "none" : "flex";

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton?.setAttribute("disabled", "disabled");
      } else {
        this.nextButton?.removeAttribute("disabled");
      }
      if (this.nextButton) this.nextButton.style.display = hideButtons ? "none" : "flex";
    }
  }

  isSlideVisible(element) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = parseInt(event.currentTarget.dataset.step) || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }

  // Touch screens have no hover, so the slide under the finger is flagged while it is being swiped
  addTouchedSlideState() {
    const touchedClass = "slider__slide--touched";
    const clearTouched = () => {
      this.slider.querySelectorAll(`.${touchedClass}`).forEach((slide) => slide.classList.remove(touchedClass));
    };

    this.slider.addEventListener("touchstart", (e) => {
      e.target.closest(".slider__slide")?.classList.add(touchedClass);
    }, { passive: true });
    this.slider.addEventListener("touchend", clearTouched, { passive: true });
    this.slider.addEventListener("touchcancel", clearTouched, { passive: true });
  }

  addClickAndDrag() {
    const DRAG_THRESHOLD = 5; // px of movement before a drag counts as a drag, not a click
    let isDown = false;
    let startX;
    let startScrollLeft;
    let maxDragDistance = 0;
    let preventClick = false;
    const interactiveSelector = 'select, input, textarea, button, label, option, [data-no-drag]';

    this.slider.addEventListener("mousedown", (e) => {
      if (window.innerWidth <= 749) return;
      if (e.target.closest(interactiveSelector)) return;
      isDown = true;
      startX = e.pageX;
      startScrollLeft = this.slider.scrollLeft;
      maxDragDistance = 0;
      this.slider.style.scrollSnapType = "none";
      this.slider.style.scrollBehavior = "auto";
      e.preventDefault();
    });

    document.addEventListener("mouseup", () => {
      if (!isDown) return;
      isDown = false;
      preventClick = maxDragDistance > DRAG_THRESHOLD;
      this.slider.style.scrollBehavior = "";
      this.slider.style.scrollSnapType = "";
      setTimeout(() => preventClick = false, 0);
    });

    document.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      const x = e.pageX;
      const walk = x - startX;
      maxDragDistance = Math.max(maxDragDistance, Math.abs(walk));
      this.slider.scrollLeft = startScrollLeft - walk;
    });

    this.slider.addEventListener("click", (e) => {
      if (preventClick) e.preventDefault();
    });
  }


  connectedCallback() {
    if (this.dataset.reset_scroll === "true") {
      this.slider.scrollLeft = 0;
    }
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    if (!this.dataset.enableSliderLooping == 'false') this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));

    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this), { passive: true });
    this.setSlideVisibility();

    if (this.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.play();
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const isActive = index === this.currentPage - 1;
      const focusables = item.querySelectorAll(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusables.length === 0) {
        item.setAttribute("aria-hidden", !isActive);
        item.setAttribute("tabindex", isActive ? "0" : "-1");
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const targetIndex = this.sliderControlLinksArray.indexOf(event.currentTarget);
    const scrollPosition = targetIndex * this.sliderItemsToShow[0].clientWidth;
    this.slider.scrollTo({ left: scrollPosition });
  }
}

customElements.define("slideshow-component", SlideshowComponent);
